import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  statesAPI,
  districtsAPI,
  citiesAPI,
  type State,
  type District,
  type City as CityRecord,
  type CityCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

function getStateName(city: CityRecord, states: State[]) {
  if (city.state_name) {
    return city.state_name;
  }
  const id = city.state;
  if (id === null) {
    return "—";
  }
  const s = states.find(p => String(p.id) === String(id));
  return s?.name ?? String(id);
}
export default function City() {
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<CityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CityCreatePayload>({
    name: "",
    state: "",
    district: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<CityRecord | null>(null);
  const [deleting, setDeleting] = useState<CityRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchStates = async () => {
    try {
      const list = await statesAPI.list();
      setStates(Array.isArray(list) ? list : []);
    } catch {
      setStates([]);
    }
  };

  const fetchAllDistricts = async () => {
    setDistrictsLoading(true);
    try {
      const list = await districtsAPI.list();
      setDistricts(Array.isArray(list) ? list : []);
    } catch {
      setDistricts([]);
    } finally {
      setDistrictsLoading(false);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await citiesAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setCities(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load cities");
      setCities([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchStates();
    fetchAllDistricts();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("City name is required");
      return;
    }
    if (!form.district) {
      toast.error("Please select a district");
      return;
    }
    setSubmitLoading(true);
    try {
      const selectedDistrict = districts.find(
        d => String(d.id) === String(form.district)
      );
      const stateId = selectedDistrict?.state ?? form.state;

      const payload = {
        name: form.name.trim(),
        state: Number(stateId) || stateId,
        district: Number(form.district) || form.district,
        is_active: form.is_active,
      };
      if (editing) {
        await citiesAPI.update(editing.id, payload);
        toast.success("City updated successfully");
      } else {
        await citiesAPI.create(payload);
        toast.success("City created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", state: "", district: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save city";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await citiesAPI.delete(deleting.id);
      toast.success("City deleted successfully");
      setDeleting(null);
      if (cities.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete city";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback((c: CityRecord) => {
    setEditing(c);
    setForm({
      name: c.name,
      state: c.state !== null ? String(c.state) : "",
      district: c.district !== null ? String(c.district) : "",
      is_active: c.is_active ?? true,
    });
    setDialogOpen(true);
  }, []);

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<CityRecord>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "district",
            label: "District",
            width: 160,
            renderCell: row => row.district_name ?? "—",
          },
          {
            id: "state",
            label: "State",
            width: 160,
            renderCell: row => getStateName(row, states),
          },
        ],
      }),
    [currentPage, pageSize, openEdit, states]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">City</h1>
        <p className="text-muted-foreground">Manage cities</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-city"
        tableColumns={tableColumns}
        listTitle={`Cities (${totalItems})`}
        searchPlaceholder="Search cities..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", state: "", district: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    state: "",
                    district: "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit City" : "Create New City"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>District *</Label>
                  <Select
                    value={String(form.district ?? "")}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, district: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          districtsLoading
                            ? "Loading districts..."
                            : "Select district"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="city-name">Name *</Label>
                  <Input
                    id="city-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="City name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="city-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="city-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-city"
            columns={tableColumns}
            data={cities}
            getRowKey={c => c.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No cities yet. Create one to get started."
                : "No cities found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete City</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
